/**
 * Housing Input Data Page — /housing/input-data
 * Source of project data creation and updates.
 * Prefills from existing journey API data. Groups inputs logically.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { RefreshCw, ArrowLeft, Save, CheckCircle2, Hash, Clock, User, AlertCircle, Info } from "lucide-react";
import { ContextualGuide } from "@/components/HousingContextualGuide";
import { toast } from "sonner";
import { invalidateHousingCache } from "@/hooks/useHousingJourney";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
function InputGroup({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-4">{title}</p>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", unit, note, readOnly = false }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}
        {unit && <span className="text-xs text-slate-400 font-normal ml-1">({unit})</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={readOnly ? undefined : (e) => onChange(name, e.target.value)}
        readOnly={readOnly}
        className={`w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:border-teal-400 transition-colors ${
          readOnly ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed" : "bg-white border-slate-200 text-slate-900"
        }`}
        placeholder={readOnly ? "Auto-calculated" : `Enter ${label.toLowerCase()}`}
        data-testid={`input-${name}`}
      />
      {note && <p className="text-xs text-slate-400 mt-1 italic">{note}</p>}
    </div>
  );
}

export default function HousingInputData() {
  const navigate = useNavigate();
  const [journey, setJourney]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [version, setVersion]   = useState(null);   // last saved version metadata
  const [history, setHistory]   = useState([]);      // version history
  const [form, setForm]         = useState({});

  useEffect(() => {
    client.get("/housing/my-journey").then(r => {
      const j = r.data;
      setJourney(j);
      if (j.version?.number) setVersion(j.version);
      const d = j.demo_unit || {};
      const i = d.interventions || {};
      const o = j.organization || {};
      setForm({
        org_name:      o.name || "Coop Green Homes Ltd",
        sector:        o.sector || "Real Estate / Green Housing",
        country:       o.country || "Nigeria",
        employee_count: o.employee_count || 12,
        floor_area_m2:  d.floor_area_m2 || 90,
        bedrooms:       d.bedrooms || 3,
        units_count:    j.programme?.units_count || 1000,
        cement_kg:      5500,
        steel_kg:       900,
        masonry_kg:     15000,
        scm_substitution_pct:      i.scm_substitution_pct     || 40,
        steel_recycled_pct:        i.steel_recycled_pct       || 50,
        solar_fraction_pct:        i.solar_fraction_pct       || 30,
        insulation_improvement_pct: i.insulation_improvement_pct || 25,
        annual_energy_kwh:         4200,
        cooling_energy_kwh:        1200,
        capex_usd:                 d.capex_usd || 4500,
        project_life_years:        30,
      });
      setLoading(false);
    }).catch(() => setLoading(false));

    // Load version history
    client.get("/housing/history").then(r => setHistory(r.data?.versions || [])).catch(() => {});
  }, []);

  const handleChange = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // POST to backend — real persistence + recalculation
      const engineInputs = {
        cement_kg:                  parseFloat(form.cement_kg) || 5500,
        steel_kg:                   parseFloat(form.steel_kg) || 900,
        masonry_kg:                 parseFloat(form.masonry_kg) || 15000,
        scm_substitution_pct:       parseFloat(form.scm_substitution_pct) || 40,
        steel_recycled_pct:         parseFloat(form.steel_recycled_pct) || 50,
        solar_fraction_pct:         parseFloat(form.solar_fraction_pct) || 30,
        insulation_improvement_pct: parseFloat(form.insulation_improvement_pct) || 25,
        annual_energy_kwh:          parseFloat(form.annual_energy_kwh) || 4200,
        cooling_energy_kwh:         parseFloat(form.cooling_energy_kwh) || 1200,
        capex_usd:                  parseFloat(form.capex_usd) || 4500,
        units_count:                parseInt(form.units_count) || 1000,
        project_life_years:         30,
        version_label:              `Manual update — ${new Date().toLocaleDateString()}`,
      };

      const token = localStorage.getItem("decarboniq_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/housing/save-inputs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(engineInputs),
      });
      if (!res.ok) throw new Error(await res.text());
      const result = await res.json();

      if (result.duplicate) {
        toast.info("Inputs unchanged — existing calculation still current.");
      } else {
        invalidateHousingCache(); // invalidate shared context after save
        setVersion({
          number:     result.version_number,
          hash:       result.calculation_hash,
          timestamp:  result.timestamp,
          methodology:result.methodology,
          updated_by: result.updated_by,
        });
        // Refresh version history
        client.get("/housing/history").then(r => setHistory(r.data?.versions || [])).catch(() => {});
        toast.success(`Version ${result.version_number} saved · Calculation hash: ${(result.calculation_hash || "").slice(0,8)}...`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Save failed — please retry.");
    }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><RefreshCw size={18} className="animate-spin text-teal-500" /></div>;

  const org  = journey?.organization || {};
  const demo = journey?.demo_unit || {};
  const projType  = journey?.project?.type       || "Low-Carbon Housing";
  const projMethod= journey?.project?.methodology || "DPCS-H v1.0";

  return (
    <div className="p-6 lg:p-8 space-y-6">
        <button onClick={() => navigate("/housing-project")} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
          <ArrowLeft size={13} /> Housing Project
        </button>

        {/* Project Type Context Panel */}
        <div className="bg-white border border-slate-200 rounded-xl px-5 py-3 mb-5 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Info size={13} className="text-teal-500 flex-shrink-0" />
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Schema Context</p>
          </div>
          {[
            ["Project Type",   projType],
            ["Methodology",    projMethod],
            ["Schema",         "Housing-specific (DPCS-H v1.0)"],
            ["Sector",         org.sector || "Real Estate / Green Housing"],
            ["Country",        org.country || "Nigeria"],
          ].map(([l, v]) => (
            <div key={l} className="flex items-center gap-1.5">
              <span className="text-xs text-slate-400">{l}:</span>
              <span className="text-xs font-bold text-slate-700">{v}</span>
            </div>
          ))}
          <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full ml-auto flex-shrink-0">
            Housing-specific fields · Multi-sector support planned (biogas, solar, industrial)
          </span>
        </div>

        <div className="flex items-start justify-between mb-6">
          <div>
            
      <PageHeader
        label="{journey?.organization?.name}"
        title="Input Data"
        subtitle="Prefilled from current assessment. Save to trigger recalculation and create a new version."
        backUrl="/housing-project"
        backLabel="Housing Project"
      />
          </div>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 disabled:opacity-50 transition-colors"
            data-testid="save-input-data">
            {saving ? <><RefreshCw size={14} className="animate-spin" /> Saving...</> : <><Save size={14} /> Save & Recalculate</>}
          </button>
        </div>

        {/* Version metadata banner */}
        {version?.number ? (
          <div className="bg-white border border-teal-200 rounded-xl px-5 py-3 mb-6 flex flex-wrap items-center gap-4"
            data-testid="version-banner">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-teal-600" />
              <p className="text-xs font-bold text-teal-800">Version {version.number} · Calculated</p>
            </div>
            <div className="flex items-center gap-1.5">
              <Hash size={11} className="text-slate-400" />
              <p className="text-xs font-mono text-slate-500">{(version.hash || "").slice(0,16)}...</p>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock size={11} className="text-slate-400" />
              <p className="text-xs text-slate-500">{version.timestamp ? new Date(version.timestamp).toLocaleString() : "—"}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <User size={11} className="text-slate-400" />
              <p className="text-xs text-slate-500">{version.updated_by || "—"}</p>
            </div>
            <p className="text-xs text-slate-400 ml-auto">{version.methodology}</p>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mb-6 flex items-center gap-2">
            <AlertCircle size={13} className="text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-700">No calculation saved yet — edit inputs and click "Save & Recalculate" to create Version 1.</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left column */}
          <div className="space-y-5">
            <InputGroup title="1. Project Profile">
              <ContextualGuide guideKey="input_project_profile" title="What verifiers check in Project Profile" />
              <Field label="Organisation Name" name="org_name" value={form.org_name} onChange={handleChange} />
              <Field label="Sector" name="sector" value={form.sector} onChange={handleChange} />
              <Field label="Country" name="country" value={form.country} onChange={handleChange} />
              <Field label="Employee Count" name="employee_count" value={form.employee_count} onChange={handleChange} type="number" note="Used for intensity metrics" />
              <Field label="Units in Programme" name="units_count" value={form.units_count} onChange={handleChange} type="number" unit="units" note="Indicative programme scale for reports" />
            </InputGroup>

            <InputGroup title="2. Baseline Assumptions">
              <ContextualGuide guideKey="input_baseline" title="Common input mistakes (baseline)" />
              <p className="text-xs text-slate-400 italic -mt-2">Standard OPC cement + BOF steel + grid electricity — no interventions</p>
              <Field label="Cement per unit" name="cement_kg" value={form.cement_kg} onChange={handleChange} type="number" unit="kg" note="Factor: 0.875 kgCO2e/kg (OPC)" />
              <Field label="Steel per unit" name="steel_kg" value={form.steel_kg} onChange={handleChange} type="number" unit="kg" note="Factor: 1.85 kgCO2e/kg (BOF)" />
              <Field label="Masonry per unit" name="masonry_kg" value={form.masonry_kg} onChange={handleChange} type="number" unit="kg" note="Factor: 0.12 kgCO2e/kg" />
              <Field label="Floor area" name="floor_area_m2" value={form.floor_area_m2} onChange={handleChange} type="number" unit="m²" />
            </InputGroup>

            <InputGroup title="3. Energy / Operational">
              <Field label="Annual grid electricity" name="annual_energy_kwh" value={form.annual_energy_kwh} onChange={handleChange} type="number" unit="kWh/yr" note="Grid emission factor: 0.43 kgCO2e/kWh (Nigeria)" />
              <Field label="Cooling energy" name="cooling_energy_kwh" value={form.cooling_energy_kwh} onChange={handleChange} type="number" unit="kWh/yr" />
            </InputGroup>
          </div>

          {/* Right column */}
          <div className="space-y-5">
            <InputGroup title="4. Project Scenario Inputs">
              <ContextualGuide guideKey="input_project_scenario" title="What verifiers check in interventions" />
              <p className="text-xs text-slate-400 italic -mt-2">Low-carbon interventions applied to the baseline</p>
              <Field label="SCM cement substitution" name="scm_substitution_pct" value={form.scm_substitution_pct} onChange={handleChange} type="number" unit="%" note="Replaces OPC with slag/fly ash. Blended factor: 0.437 kgCO2e/kg" />
              <Field label="Recycled steel content" name="steel_recycled_pct" value={form.steel_recycled_pct} onChange={handleChange} type="number" unit="%" note="EAF route. Recycled factor: 0.35 kgCO2e/kg" />
              <Field label="Solar PV grid offset" name="solar_fraction_pct" value={form.solar_fraction_pct} onChange={handleChange} type="number" unit="%" note="Reduces annual grid consumption" />
              <Field label="Insulation improvement" name="insulation_improvement_pct" value={form.insulation_improvement_pct} onChange={handleChange} type="number" unit="%" note="Reduces operational energy use" />
            </InputGroup>

            <InputGroup title="5. Financial Inputs">
              <Field label="Incremental capex" name="capex_usd" value={form.capex_usd} onChange={handleChange} type="number" unit="USD/unit" note="Additional cost vs conventional construction" />
            </InputGroup>

            {/* Calculated outputs (read-only) */}
            <InputGroup title="Calculated Outputs (from current data)">
              <p className="text-xs text-slate-400 italic -mt-2">These are derived from the values above — update inputs to change outputs</p>
              <Field label="Baseline embodied" name="baseline_embodied" value={demo.baseline_embodied_tco2e} onChange={() => {}} unit="tCO2e/unit" readOnly note="Auto-calculated" />
              <Field label="Project embodied" name="project_embodied" value={demo.project_embodied_tco2e} onChange={() => {}} unit="tCO2e/unit" readOnly />
              <Field label="Embodied reduction" name="embodied_reduction" value={demo.embodied_reduction_tco2e} onChange={() => {}} unit="tCO2e/unit" readOnly />
              <Field label="Annual operational reduction" name="op_reduction" value={demo.operational_reduction_tco2e_yr} onChange={() => {}} unit="tCO2e/yr" readOnly />
            </InputGroup>
          </div>
        </div>

        <p className="text-xs text-slate-400 italic mt-6 text-center">
          Changes trigger DPCS-H recalculation. Each save creates an immutable versioned record with SHA-256 hash.
          Same inputs produce the same hash — guarantee of reproducibility.
        </p>

        {/* Version history */}
        {history.length > 0 && (
          <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="version-history">
            <div className="px-5 py-4 border-b border-slate-100">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Version History</p>
            </div>
            <div className="divide-y divide-slate-50">
              {history.map(v => (
                <div key={v.version_number} className="px-5 py-3 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded">
                      v{v.version_number}
                    </span>
                    <p className="text-xs font-semibold text-slate-700">{v.version_label}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="font-mono">{(v.calculation_hash || "").slice(0,12)}...</span>
                    <span>{v.calculated_at ? new Date(v.calculated_at).toLocaleDateString() : "—"}</span>
                    <span>{v.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
}
